/**
 * Call Ended View Component
 * Displays after call ends with duration and rejoin option
 * Responsive: fits container height properly
 */

import React from "react";
import { Button } from "@/components/ui/button";
import { Phone, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  videoCallMutedOverlayClassName,
  videoCallPanelClassName,
  videoCallSurfaceClassName,
} from "./video-call-theme";

interface CallEndedViewProps {
  callDuration: string;
  onRejoin: () => void;
  isRejoining?: boolean;
  className?: string;
}

export const CallEndedView: React.FC<CallEndedViewProps> = ({
  callDuration,
  onRejoin,
  isRejoining = false,
  className,
}) => {
  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center rounded-lg p-4 text-center md:p-6",
        videoCallSurfaceClassName,
        className
      )}
    >
      <div className="relative mb-3 md:mb-4">
        <div
          className={cn(
            "flex h-14 w-14 items-center justify-center rounded-full border border-border md:h-20 md:w-20",
            videoCallMutedOverlayClassName
          )}
        >
          <Phone className="h-6 w-6 text-muted-foreground md:h-8 md:w-8" />
        </div>
      </div>

      <div
        className={cn("mb-3 w-full max-w-sm md:mb-4", videoCallPanelClassName)}
      >
        <div className="space-y-1 p-4 md:p-5">
          <p className="text-xl font-bold text-foreground md:text-2xl">
            {callDuration}
          </p>
          <p className="text-xs text-muted-foreground md:text-sm">
            You just ended the consultation.
          </p>
        </div>
      </div>

      <Button
        onClick={onRejoin}
        disabled={isRejoining}
        size="sm"
        className="rounded-full px-4 md:px-6"
      >
        {isRejoining ? (
          <span className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 animate-spin" />
            Rejoining...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            Rejoin Call
          </span>
        )}
      </Button>
    </div>
  );
};

export default CallEndedView;
